<script setup>
import { computed, nextTick, onMounted, provide, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useKanbanBoard } from 'dashboard/composables/useKanbanBoard';
import NextButton from 'dashboard/components-next/button/Button.vue';
import KanbanFilterBar from '../components/KanbanFilterBar.vue';
import KanbanColumn from '../components/KanbanColumn.vue';
import KanbanConversationModal from '../components/KanbanConversationModal.vue';
import KanbanFunnelManager from '../components/KanbanFunnelManager.vue';
import KanbanFunnelSwitcher from '../components/KanbanFunnelSwitcher.vue';

const { t } = useI18n();
const store = useStore();
// Escrita de funil/etapa é ADMINISTRATOR-ONLY no backend (DebtKanbanPolicy#manage?,
// 401 para agente). O gatilho da configuração some para quem não é
// administrador: oferecer um botão que sempre falha é pior do que não
// oferecê-lo — o agente continua vendo, filtrando e movendo card, que é o
// trabalho dele.
const { isAdmin } = useAdmin();

// Sidebar.vue despacha labels/inboxes/teams para todas as rotas, mas não
// agents — o filtro "Assignee" desta página depende de agents/getAgents,
// que nasce vazio sem isto (A3). Despachado aqui, não no Sidebar, porque
// Sidebar serve todas as rotas e não só o board.
onMounted(() => store.dispatch('agents/get'));

const {
  funnels,
  funnelsLoaded,
  funnelsError,
  selectedFunnelId,
  selectedFunnel,
  stages,
  periodDays,
  filters,
  hasActiveFilters,
  columnsByStageId,
  hasLoadedBoardOnce,
  boardError,
  isBoardEmptyByFilters,
  selectFunnel,
  setPeriodDays,
  setFilter,
  setSearchQuery,
  clearFilters,
  loadMoreColumn,
  moveCard,
  notifyColumnChange,
  retry,
  retryFunnels,
  reloadAfterFunnelAdmin,
} = useKanbanBoard();

// BLOQUEADOR 1 (revisor-frontend, T3.4, 2.1.1): alternativa por teclado ao
// arrastar (SortableJS não opera por teclado). Quem move o card entre as
// listas é o composable (relocateCard, dono único de cards/contagem/cursor
// de cada coluna) — esta página só declara a intenção. KanbanColumn.vue
// (intermediário entre esta página e KanbanCard) está sob edição concorrente
// de outro agente e não pode receber props novas — por isso stages e esta
// função atravessam por provide/inject.

// 2.4.3/2.4.7: mover por teclado DESMONTA o card — cada coluna tem seu
// próprio Draggable, então sair de uma e entrar na outra destrói a instância
// (e o botão que abriu o painel junto). Focar o gatilho dentro do próprio
// card, depois de disparar o movimento, foca um elemento que sai do DOM no
// flush seguinte: o foco acaba em BODY e quem navega por teclado perde o
// lugar. Por isso o pedido de foco viaja com o id do card e é cumprido pela
// instância NOVA, ao montar (ver onMounted em KanbanCard.vue).
//
// O pedido só é zerado DEPOIS que o movimento se resolve, e não pela
// primeira instância que o cumpre: quando o PATCH falha, o composable
// reverte o card para a origem e é a instância remontada LÁ que precisa do
// foco (achado A3 do refutador — o foco caía em BODY em toda falha de rede).
// nextTick antes de zerar dá ao Vue a chance de montar essa instância; sem
// ele o pedido morreria antes de ser cumprido.
const focusCardIdAfterMove = ref(null);

const moveCardToStage = async (card, targetStageId) => {
  const originStageId = card.stage_id;
  if (originStageId === targetStageId) return;

  focusCardIdAfterMove.value = card.id;
  await moveCard({ card, targetStageId, originStageId });
  await nextTick();
  focusCardIdAfterMove.value = null;
};

provide('kanbanStages', stages);
provide('kanbanMoveCardToStage', moveCardToStage);
provide('kanbanFocusCardIdAfterMove', focusCardIdAfterMove);

// Cor das etiquetas do card. A conversa traz só os TÍTULOS
// (cached_label_list_array em _card.json.jbuilder); a cor mora na lista de
// etiquetas da conta, que o Sidebar já despacha para todas as rotas. Lida uma
// vez aqui e injetada, como stages e as ações: são até 125 cards na tela, e
// cada um lendo o mesmo getter seria a mesma lista N vezes.
provide('kanbanAccountLabels', useMapGetter('labels/getLabels'));

// Ler a conversa sem sair do board. O diálogo mora AQUI, e não dentro do
// card, por dois motivos: só há um aberto por vez (montá-lo por card seria
// uma sobreposição por conversa carregada na tela), e o card é desmontado a
// cada movimento entre colunas — um diálogo ancorado nele morreria junto.
// O card só declara a intenção, como já faz para mover (provide/inject
// atravessando KanbanColumn).
const conversationModalCard = ref(null);
const openConversation = card => {
  conversationModalCard.value = card;
};
const closeConversation = () => {
  conversationModalCard.value = null;
};
provide('kanbanOpenConversation', openConversation);

// Configuração de funis e etapas (T3.5). O diálogo é montado pela página,
// como o da conversa: só há um aberto por vez, e ele precisa sobreviver a
// qualquer recomposição das colunas.
const isFunnelManagerOpen = ref(false);
const openFunnelManager = () => {
  isFunnelManagerOpen.value = true;
};

// Recarrega funis E colunas só quando a configuração mudou alguma coisa: uma
// etapa nova não passa pelo watch de filtros do composable, e abrir/fechar sem
// escrever não deve custar um refetch do board.
const closeFunnelManager = changed => {
  isFunnelManagerOpen.value = false;
  if (changed) reloadAfterFunnelAdmin();
};

// Nenhum funil configurado. O estado deixou de ser um beco sem saída: para o
// administrador ele abre a configuração; para o agente ele diz de quem é a
// ação, em vez de oferecer um botão que responderia 401.
const hasNoFunnel = computed(
  () => funnelsLoaded.value && !funnelsError.value && funnels.value.length === 0
);

// Funil selecionado sem NENHUMA etapa: o board devolve zero coluna e a área
// central ficava em branco, sem nada que explicasse a tela vazia (o estado de
// "vazio por filtros" exige stages.length > 0, então nenhum ramo casava).
const hasNoStage = computed(
  () =>
    !hasNoFunnel.value &&
    funnelsLoaded.value &&
    !funnelsError.value &&
    Boolean(selectedFunnel.value) &&
    stages.value.length === 0
);

// Erro de carga: tanto a lista de funis quanto o board propriamente dito
// podem falhar, e cada um se recupera chamando a ação correta — reaplicar
// retry() sobre uma falha de funnelsError() nunca dispararia nova consulta
// de funis, deixando o board preso no erro para sempre.
const hasError = computed(() => funnelsError.value || boardError.value);
const retryLoad = () => (funnelsError.value ? retryFunnels() : retry());

// !hasLoadedBoardOnce cobre também a janela entre o mount e a resposta de
// funnels() (A5): antes dela, selectedFunnelId ainda não existe, fetchBoard
// nunca roda e isBoardLoading nunca vira true — sem isto nenhum dos ramos do
// template casava e a primeira carga mostrava uma área em branco.
const showSkeleton = computed(
  () =>
    !hasLoadedBoardOnce.value &&
    !hasError.value &&
    !hasNoFunnel.value &&
    !hasNoStage.value
);

const showColumns = computed(
  () =>
    !hasNoFunnel.value &&
    !hasError.value &&
    !showSkeleton.value &&
    stages.value.length > 0 &&
    !isBoardEmptyByFilters.value
);

const dragGroup = computed(() => `kanban-board-${selectedFunnelId.value}`);

const onColumnChange = (stageId, evt) => notifyColumnChange(stageId, evt);
</script>

<template>
  <div class="flex flex-col w-full h-full min-w-0 min-h-0">
    <!--
      `w-full min-w-0` NÃO é decorativo: é o conserto do corte da última coluna.
      Esta div é filha flex direta de <main> (Dashboard.vue:143, `flex flex-1
      w-full overflow-hidden`) e, sem largura declarada, resolvia pela largura do
      CONTEÚDO — não pela do <main>. Medido em Chrome (5 etapas, cards com prévia
      e etiquetas, pt-BR): em viewport 1480 o <main> tinha 1281px e esta div
      1551px; o excedente de 270px era cortado pelo `overflow-hidden` do <main>,
      que não rola. A quinta coluna aparecia com 40 de seus 294px e não havia
      como alcançá-la: o `overflow-x-auto` das colunas media scrollWidth ==
      clientWidth (nada a rolar do ponto de vista dele — quem estourava era o
      ancestral). O botão de configuração do cabeçalho saía da área visível pelo
      mesmo motivo.
      Com `w-full min-w-0` a largura passa a vir do <main> (mesmo idioma de
      ConversationView.vue:198, `flex w-full h-full min-w-0`), as colunas voltam a
      caber dentro dele e, quando não cabem, quem rola é o contêiner das colunas.

      O comentário fica DENTRO da raiz de propósito: comentário ANTES do elemento
      raiz transforma o componente em fragmento de múltiplas raízes, e
      wrapper.classes() passa a ler o nó de comentário (mesma armadilha já
      documentada em KanbanColumn.vue).
    -->
    <!--
      Sem `justify-between`: o gatilho da configuração de funis/etapas ficava no
      canto DIREITO do cabeçalho, e o dono não o achava. Agora ele é vizinho do
      funil que configura — a mesma linha que diz QUAL funil está na tela diz
      onde se mexe nele.
    -->
    <div
      data-testid="kanban-board-header"
      class="flex items-center gap-3 px-4 py-3 shrink-0"
    >
      <div class="flex items-center gap-2 min-w-0">
        <!--
          O seletor vem ANTES do nome, e não depois: o rótulo fixo dele
          ("Funil") é o que nomeia o conceito na tela. Sem isso, o board
          mostrava um h1 com o nome do funil e cinco colunas com nome de etapa,
          e nada dizia que a relação entre os dois era funil -> etapas.

          E ele aparece com UM funil também (antes: `funnels.length > 1`). Em
          produção existe um único funil, então o conceito de funil não tinha
          NENHUMA representação na interface — o dono via cinco etapas e
          concluiu, corretamente, que funil não existia ali. Com um funil só a
          lista traz esse funil marcado como atual: não é lista vazia nem
          controle quebrado, e é por ela que se vê o que existe.
        -->
        <KanbanFunnelSwitcher
          v-if="funnels.length"
          :funnels="funnels"
          :model-value="selectedFunnelId"
          @update:model-value="selectFunnel"
        />
        <!--
          ALTO (revisor-frontend, T3.4): antes, o h1 (109px) truncava
          "Amigável —…" ao lado de um <select> nativo (230,7px) mostrando o
          MESMO nome por inteiro — informação duplicada a 2cm, e o elemento
          de maior hierarquia era o único sacrificado. :title cobre a
          legibilidade do truncamento (mesmo padrão do h3 de
          KanbanColumn.vue); o switcher deixou de repetir o nome do funil —
          ver KanbanFunnelSwitcher.vue.
        -->
        <h1
          v-if="selectedFunnel"
          class="m-0 text-base font-semibold truncate text-n-slate-12"
          :title="selectedFunnel.name"
        >
          {{ selectedFunnel.name }}
        </h1>
        <!--
          Fecha a hierarquia sem legenda: "Funil | <nome> | Etapas: 5" nomeia a
          relação entre o cabeçalho e as colunas que vêm abaixo, que são
          exatamente essas etapas.

          `hidden sm:inline` é medido, não cautela: em viewport 360 esta
          contagem ocupa 50,8px (mais 8 de gap) e o h1 do funil, que é quem cede
          espaço (só ele tem `truncate` e o grupo tem `min-w-0`), caía para
          13,4px — o NOME do funil sumia da tela para caber a contagem, que é o
          dado menos importante dos dois. Escondida abaixo de sm, ela devolve o
          h1 a 72,2px (nome truncado, recuperável pelo :title). O rótulo do
          seletor ("Funil") continua nomeando o conceito em qualquer largura.
        -->
        <span
          v-if="stages.length"
          data-testid="kanban-stage-count"
          class="hidden text-xs shrink-0 sm:inline text-n-slate-11"
        >
          {{ t('KANBAN.BOARD.STAGE_COUNT', { count: stages.length }) }}
        </span>
      </div>

      <NextButton
        v-if="isAdmin"
        slate
        faded
        xs
        class="shrink-0"
        icon="i-lucide-settings-2"
        data-testid="kanban-manage-funnels"
        :label="t('KANBAN.ADMIN.OPEN')"
        @click="openFunnelManager"
      />
    </div>

    <KanbanFilterBar
      v-if="!hasNoFunnel"
      :filters="filters"
      :period-days="periodDays"
      :has-active-filters="hasActiveFilters"
      :set-filter="setFilter"
      :set-period-days="setPeriodDays"
      :set-search-query="setSearchQuery"
      :clear-filters="clearFilters"
    />

    <div class="flex-1 min-h-0 overflow-hidden">
      <!--
        ALTO (revisor-frontend, T3.4, 4.1.3): só o esqueleto tinha
        role=status. Quando ele some e vira um destes três estados
        terminais, quem usa leitor de tela não recebia nada — a tela parava
        de carregar em silêncio. hasError usa role=alert (assertivo: é uma
        falha); os outros dois, role=status (polido: mudança de estado, não
        emergência).
      -->
      <div
        v-if="hasNoFunnel"
        role="status"
        class="flex flex-col items-center justify-center h-full gap-1 px-4 text-center"
      >
        <h2 class="m-0 text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.BOARD.NO_FUNNEL.TITLE') }}
        </h2>
        <p class="m-0 text-sm text-n-slate-11 max-w-md">
          {{
            isAdmin
              ? t('KANBAN.BOARD.NO_FUNNEL.DESCRIPTION_ADMIN')
              : t('KANBAN.BOARD.NO_FUNNEL.DESCRIPTION')
          }}
        </p>
        <!-- !text-n-blue-12: ver a justificativa medida em
             KanbanDebthubLinkPanel.vue (a variante `blue faded` compartilhada
             reprova 1.4.3 no tema claro; o override não toca o token). -->
        <NextButton
          v-if="isAdmin"
          blue
          faded
          xs
          class="mt-1 !text-n-blue-12"
          data-testid="kanban-no-funnel-action"
          :label="t('KANBAN.BOARD.NO_FUNNEL.CREATE_ACTION')"
          @click="openFunnelManager"
        />
      </div>

      <!-- Funil sem etapa: o board devolve zero coluna, e antes desta unidade
           a área central ficava simplesmente em branco. -->
      <div
        v-else-if="hasNoStage"
        role="status"
        class="flex flex-col items-center justify-center h-full gap-1 px-4 text-center"
      >
        <h2 class="m-0 text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.BOARD.NO_STAGE.TITLE') }}
        </h2>
        <p class="m-0 text-sm text-n-slate-11 max-w-md">
          {{
            isAdmin
              ? t('KANBAN.BOARD.NO_STAGE.DESCRIPTION_ADMIN')
              : t('KANBAN.BOARD.NO_STAGE.DESCRIPTION')
          }}
        </p>
        <NextButton
          v-if="isAdmin"
          blue
          faded
          xs
          class="mt-1 !text-n-blue-12"
          data-testid="kanban-no-stage-action"
          :label="t('KANBAN.BOARD.NO_STAGE.CREATE_ACTION')"
          @click="openFunnelManager"
        />
      </div>

      <div
        v-else-if="hasError"
        role="alert"
        class="flex flex-col items-center justify-center h-full gap-2 px-4 text-center"
      >
        <h2 class="m-0 text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.BOARD.ERROR.TITLE') }}
        </h2>
        <NextButton
          slate
          faded
          xs
          :label="t('KANBAN.BOARD.ERROR.RETRY')"
          @click="retryLoad"
        />
      </div>

      <div
        v-else-if="showSkeleton"
        class="flex items-start h-full gap-3 px-4 py-3 overflow-hidden"
        role="status"
        :aria-label="t('KANBAN.BOARD.LOADING')"
      >
        <!-- Mesma geometria elástica das colunas reais (KanbanColumn.vue):
             o esqueleto com largura fixa saltava de 280px para a largura
             elástica no instante em que os dados chegavam. -->
        <div
          v-for="index in 5"
          :key="index"
          class="flex flex-col flex-1 min-w-[240px] max-w-[420px] gap-2"
        >
          <div class="h-4 w-24 rounded bg-n-alpha-2 animate-pulse" />
          <div
            v-for="cardIndex in 3"
            :key="cardIndex"
            class="h-24 rounded-lg bg-n-alpha-2 animate-pulse"
          />
        </div>
      </div>

      <div
        v-else-if="isBoardEmptyByFilters"
        role="status"
        class="flex flex-col items-center justify-center h-full gap-2 px-4 text-center"
      >
        <h2 class="m-0 text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.BOARD.EMPTY_BY_FILTERS.TITLE') }}
        </h2>
        <p class="m-0 text-sm text-n-slate-11 max-w-md">
          {{ t('KANBAN.BOARD.EMPTY_BY_FILTERS.DESCRIPTION') }}
        </p>
        <NextButton
          slate
          faded
          xs
          :label="t('KANBAN.BOARD.EMPTY_BY_FILTERS.CLEAR_ACTION')"
          @click="clearFilters"
        />
      </div>

      <div
        v-else-if="showColumns"
        class="flex items-stretch h-full gap-3 px-4 py-3 overflow-x-auto"
      >
        <KanbanColumn
          v-for="stage in stages"
          :key="stage.id"
          :stage="stage"
          :column="
            columnsByStageId[stage.id] || {
              cards: [],
              count: 0,
              countCapped: false,
              hasMore: false,
              isLoadingMore: false,
              loadMoreError: false,
            }
          "
          :drag-group="dragGroup"
          @load-more="loadMoreColumn"
          @change="evt => onColumnChange(stage.id, evt)"
        />
      </div>
    </div>

    <KanbanConversationModal
      v-if="conversationModalCard"
      :card="conversationModalCard"
      @close="closeConversation"
    />

    <KanbanFunnelManager
      v-if="isFunnelManagerOpen"
      @close="closeFunnelManager"
    />
  </div>
</template>
